import { addDays, differenceInCalendarDays, format, isValid, max, parseISO } from 'date-fns';

import { formatDateLong } from 'Helpers/dateFormat';
import { defaultDatePickerValueFormat } from 'Helpers/dateUtils';

const START_TO_START = 'start_to_start';
const FINISH_TO_START_GAP = 1;

const toDate = (value) => {
    if (!value) {
        return null;
    }

    const date = parseISO(String(value).substring(0, 10));

    return isValid(date) ? date : null;
};

/**
 * The date a predecessor row lets its successor start. Dates are inclusive days, so finish-to-start
 * frees the successor the day after the finish: finish + 1 + lag. Start-to-start is start + lag, where
 * a milestone's start is its single date, its finish. Must match ProjectActivityEarliestStartService.
 */
const constraintDate = (row) => {
    const activity = row?.activity;

    if (!activity) {
        return null;
    }

    const finish = toDate(activity.estimated_completion_date) || toDate(activity.target_completion_date);
    const startToStart = row.type === START_TO_START;
    const anchor = startToStart && !activity.is_milestone ? toDate(activity.start_date) : finish;
    const gap = startToStart ? 0 : FINISH_TO_START_GAP;

    return anchor ? addDays(anchor, gap + (Number(row.lag_days) || 0)) : null;
};

/** The latest constraint date over the predecessor rows as 'yyyy-MM-dd', or null without one. */
export const earliestStartDate = (rows = []) => {
    const dates = rows.map(constraintDate).filter(Boolean);

    return dates.length ? format(max(dates), defaultDatePickerValueFormat) : null;
};

/** Days the start falls before the earliest start, or null when it does not. */
export const startConflictDays = (startDate, earliest) => {
    const start = toDate(startDate);
    const allowed = toDate(earliest);

    if (!start || !allowed) {
        return null;
    }

    const days = differenceInCalendarDays(allowed, start);

    return days > 0 ? days : null;
};

/** Whether the start needs no move: the activity is complete or the start does not precede the earliest start. */
export const startIsClear = (startDate, earliest, isComplete) => !!isComplete || startConflictDays(startDate, earliest) === null;

/** The activity's dates moved by the given number of days. The estimated date moves only when set. */
export const shiftDates = (values, days) => {
    const shift = (value) => {
        const date = toDate(value);

        return date ? format(addDays(date, days), defaultDatePickerValueFormat) : value;
    };

    return {
        start_date: shift(values.start_date),
        target_completion_date: shift(values.target_completion_date),
        estimated_completion_date: values.estimated_completion_date ? shift(values.estimated_completion_date) : values.estimated_completion_date,
    };
};

export const startConflictMessage = (days, earliest) =>
    days === 1
        ? $t('Starts 1 day before predecessors allow ({{date}})', { date: formatDateLong(earliest) })
        : $t('Starts {{count}} days before predecessors allow ({{date}})', { count: days, date: formatDateLong(earliest) });
